export interface SubscriptionPricingConfig {
  planName: string;
  monthlyPriceAud: number;
  billingPeriodMonths: number;
  annualPriceAud: number;
  currency: string;
  currencySymbol: string;
  trialDays: number;
  renewalWarningDays: number;
  gracePeriodDays: number;
  seatsPerUnit: {
    owners: number;
    developers: number;
    stakeholders: number;
  };
  display: {
    priceTag: string;
    billingSubtitle: string;
    description: string;
    renewalBannerText: string;
    addSeatsText: string;
  };
}

export const DEFAULT_SUBSCRIPTION_PRICING: SubscriptionPricingConfig = {
  planName: 'Sprinta Annual Team Plan',
  monthlyPriceAud: 60,
  billingPeriodMonths: 12,
  annualPriceAud: 720,
  currency: 'AUD',
  currencySymbol: '$',
  trialDays: 15,
  renewalWarningDays: 30,
  gracePeriodDays: 15,
  seatsPerUnit: {
    owners: 1,
    developers: 4,
    stakeholders: 3
  },
  display: {
    priceTag: '$60 / mo',
    billingSubtitle: 'Billed annually ($720 / year per unit)',
    description: 'Covers 1 Project 4 Developers plus Owner, and 3 Stakeholders per unit',
    renewalBannerText: 'Renew Subscription ($60/mo billed annually)',
    addSeatsText: 'Add subscription units ($60/mo billed annually)'
  }
};

export const SUBSCRIPTION_CONSTANTS = {
  UNIT_PRICE_AUD: DEFAULT_SUBSCRIPTION_PRICING.annualPriceAud, // 720
  MONTHLY_PRICE_AUD: DEFAULT_SUBSCRIPTION_PRICING.monthlyPriceAud, // 60
  BILLING_PERIOD_MONTHS: DEFAULT_SUBSCRIPTION_PRICING.billingPeriodMonths, // 12
  OWNER_SEATS_PER_UNIT: DEFAULT_SUBSCRIPTION_PRICING.seatsPerUnit.owners,
  DEV_SEATS_PER_UNIT: DEFAULT_SUBSCRIPTION_PRICING.seatsPerUnit.developers,
  STAKEHOLDER_SEATS_PER_UNIT: DEFAULT_SUBSCRIPTION_PRICING.seatsPerUnit.stakeholders,
  TRIAL_DAYS: DEFAULT_SUBSCRIPTION_PRICING.trialDays,
  RENEWAL_WARNING_DAYS: DEFAULT_SUBSCRIPTION_PRICING.renewalWarningDays,
  GRACE_PERIOD_DAYS: DEFAULT_SUBSCRIPTION_PRICING.gracePeriodDays
};

export type SubscriptionStatus = 'trial' | 'active' | 'expiring_soon' | 'grace_period' | 'blocked';

export interface SubscriptionSeatUsage {
  ownersUsed: number;
  developersUsed: number;
  stakeholdersUsed: number;
  ownersCapacity: number;
  developersCapacity: number;
  stakeholdersCapacity: number;
  unitsRequired: number;
  unitsPurchased: number;
  isOverCapacity: boolean;
  extraUnitsNeeded: number;
}

export interface PaymentMethod {
  cardBrand: string;
  last4: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName: string;
}

export interface ProjectSubscription {
  id: string;
  projectReference: string;
  projectId?: string;
  status: SubscriptionStatus;
  units: number;
  annualCost: number;
  currency: string;
  trialStartDate: string;
  trialEndDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  gracePeriodEnd: string;
  daysRemaining: number;
  autoRenew: boolean;
  lastPaymentDate?: string;
  lastPaymentReference?: string;
  paymentMethod?: PaymentMethod;
}

export interface SubscriptionInvoice {
  invoiceNumber: string;
  date: string;
  amount: number;
  units: number;
  status: 'paid' | 'pending' | 'failed';
  nabReceipt: string;
  cardLast4: string;
  description: string;
}

export interface PreparePaymentResponse {
  merchantId: string;
  referenceId: string;
  transactionType: string;
  amount: string;
  timestamp: string;
  fingerprint: string;
  itemName: string;
  itemCode: string;
  price: string;
  referringCode?: string;
}

export interface NabDirectPostPayload {
  EPS_MERCHANT: string;
  EPS_TXNTYPE: string;
  EPS_REFERENCEID: string;
  EPS_AMOUNT: string;
  EPS_TIMESTAMP: string;
  EPS_FINGERPRINT: string;
  EPS_RESULTURL: string;
  EPS_CARDNUMBER: string;
  EPS_CARDNAME: string;
  EPS_EXPIRYMONTH: string;
  EPS_EXPIRYYEAR: string;
  EPS_CCV: string;
  [key: string]: string;
}

export interface ProcessPaymentResponse {
  confirmation: boolean;
  message: string;
  details?: {
    transactionId: string;
    customer?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      company?: string;
    };
    payment?: {
      pan: string;
    };
    item?: {
      itemName: string;
      itemCode: string;
      price: string;
    };
    custom?: {
      referringCode?: string;
      refid?: string;
    };
  };
}

export interface NabTransactPaymentRequest {
  cardNumber: string;
  cardholderName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  amount: number;
  currency: string;
  merchantReference: string;
  postcode?: string;
}

export interface NabTransactPaymentResponse {
  successful: boolean;
  responseCode: string; // '000' = Approved
  responseMessage: string;
  transactionId: string;
  settlementDate: string;
  receiptNumber: string;
  cardType?: string;
  last4?: string;
}
